import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {ReactiveFormsModule} from '@angular/forms';
import {MapService} from '../api/services/map.service';
import {OpenaiService} from '../api/services/openai.service';
import {PreflightResponse} from '../api/resource/openai.resource';
import {ConfirmationDialogComponent} from './confirmation-dialog.component';
import {JobService} from '../api/services/job.service';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Router} from "@angular/router";
import {Job} from '../api/interfaces/jobs.dto';
import {NotificationService} from '../services/notification.service';
import {CreditApiService} from '../api/services/credit.service';
import {GenerationStateService} from '../services/generation-state.service';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {isPdfTooLargeError, PDF_TOO_LARGE_JOB_PREFIX, showPdfTooLargeNotification} from './pdf-too-large.util';
import {SmapTaskKind, smapTaskKey} from '@maporium-workspace/shared';

export interface ImportDialogData {
  importType: 'smap' | 'pdf';
}

@Component({
  selector: 'maporium-import-map-dialog',
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0"><span class="material-symbols-rounded me-2">{{ getIcon() }}</span>
        {{ getTitle() }}
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close [matTooltip]="'EMBED_DIALOG.TOOLTIP_CLOSE_PANEL' | translate"></span>
    </div>
    <div mat-dialog-content>
        <div class="file-upload-area">
          <p class="file-instructions" *ngIf="selectedFileName">{{ selectedFileName }}</p>
          <input #fileInput type="file" name="file" id="file" [accept]="getAcceptType()" hidden (change)="onFileSelected()">
          <button type="button" class="choose-file-btn" (click)="fileInput.click()">
            {{ (selectedFileName ? 'CREATE_MAP_DIALOG.CHANGE_FILE' : 'CREATE_MAP_DIALOG.CHOOSE_FILE') | translate }}
          </button>
        </div>
    </div>
    <div mat-dialog-actions class="d-flex align-items-center justify-content-evenly">
      <button class="dialog-action-btn dialog-action-btn-cancel" mat-dialog-close [disabled]="isImporting">{{ 'CREATE_MAP_DIALOG.CANCEL' | translate }}</button>
      <button class="dialog-action-btn dialog-action-btn-primary" (click)="importMap()" [disabled]="isImporting">
        {{ isImporting ? ('CREATE_MAP_DIALOG.UPLOADING' | translate) : ('IMPORT_MAP.IMPORT' | translate) }}
      </button>
    </div>
  `,
  imports: [
    MatDialogModule,
    CommonModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatIconModule,
    TranslatePipe
  ],
  styles: [`
    .file-upload-area {
      text-align: center;
      padding: 24px 16px;
    }

    .file-instructions {
      color: #3D85C6;
      font-size: 13px;
      font-weight: 500;
      margin: 0 0 16px 0;
      word-break: break-word;
    }

    .choose-file-btn {
      font-family: 'Oswald', sans-serif;
      font-size: 15px;
      letter-spacing: 0.5px;
      padding: 0 24px;
      border: 1.5px solid #3D85C6;
      border-radius: 6px;
      background-color: white;
      color: #3D85C6;
      cursor: pointer;
      text-transform: uppercase;
      height: 29px;
      box-sizing: border-box;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    .choose-file-btn:hover {
      background-color: #f0f5fa;
    }
  `],
  standalone: true
})

export class ImportMapDialogComponent {
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  selectedFileName = '';
  isImporting = false;

  onFileSelected() {
    const files = this.fileInputRef?.nativeElement?.files;
    this.selectedFileName = files?.length ? files[0].name : '';
  }

  constructor(
    private mapService: MapService,
    private openaiService: OpenaiService,
    private jobService: JobService,
    private router: Router,
    private notificationService: NotificationService,
    private matDialogRef: MatDialogRef<ImportMapDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImportDialogData,
    private creditApiService: CreditApiService,
    private dialog: MatDialog,
    private generationStateService: GenerationStateService,
    private translate: TranslateService
  ) {
  }

  getTitle(): string {
    return this.data?.importType === 'pdf'
      ? this.translate.instant('IMPORT_MAP.TITLE_PDF')
      : this.translate.instant('IMPORT_MAP.TITLE_SMAP');
  }

  getIcon(): string {
    return this.data?.importType === 'pdf' ? 'picture_as_pdf' : 'archive';
  }

  getAcceptType(): string {
    return this.data?.importType === 'pdf' ? '.pdf' : '.smap';
  }

  getButtonText(): string {
    return this.data?.importType === 'pdf' ? 'Import' : 'Import';
  }

  importMap() {
    if (this.isImporting) return;
    if(!this.fileInputRef.nativeElement.files) {
      this.notificationService.warning(this.translate.instant('IMPORT_MAP.SELECT_FILE_WARNING'));
      return;
    }
    const file = this.fileInputRef.nativeElement.files[0];

    this.isImporting = true;
    if (this.data?.importType === 'pdf') {
      this.handlePdfImport(file);
    } else {
      this.handleSmapImport(file);
    }
  }

  private handleSmapImport(file: File) {
    const progressNotification = this.notificationService.loading(this.translate.instant(smapTaskKey('smapImport', 'UPLOADING')));

    this.mapService.importMapFromZip(file).subscribe({
      next: (job: Job) => {
        // Morph the upload card into the "in progress" card (same id) instead
        // of swapping two look-alike cards, which reads as a flash.
        this.notificationService.update(progressNotification.id, {
          type: 'info',
          message: this.translate.instant(smapTaskKey('smapImport', 'IN_PROGRESS_INFO')),
          duration: 8000,
          dismissible: true,
          taskKey: job.id
        });
        this.trackImportJob(job, 'smapImport');
        this.matDialogRef.close();
      },
      error: (error: any) => {
        this.isImporting = false;
        progressNotification.dismiss();
        this.notificationService.error(this.translate.instant(smapTaskKey('smapImport', 'START_FAILED')), {
          title: this.translate.instant(smapTaskKey('smapImport', 'FAILED_TITLE')),
          duration: 0 // Persistent until dismissed
        });
        console.error('Smap import error:', error);
      }
    });
  }

  private trackImportJob(job: Job, task: SmapTaskKind) {
    this.jobService.trackJob(job.id, (updatedJob: Job) => {
      if (updatedJob.status === 'COMPLETED' || updatedJob.status === 'FAILED') {
        this.generationStateService.endSmapGeneration(updatedJob.id);
      }
      if (updatedJob.status === 'COMPLETED') {
        const partialPercent = updatedJob.result?.rawData?.truncation?.partialPercent;
        if (typeof partialPercent === 'number' && partialPercent < 100) {
          this.notificationService.info(
            this.translate.instant('CREATE_MAP_DIALOG.PDF_PARTIAL_MESSAGE', {percent: partialPercent}),
            {title: this.translate.instant('CREATE_MAP_DIALOG.PDF_PARTIAL_SMAPPED_TITLE'), duration: 0}
          );
        }
        this.notificationService.success(
          this.translate.instant(smapTaskKey(task, 'SUCCESS_MESSAGE')),
          {
            title: this.translate.instant(smapTaskKey(task, 'SUCCESS_TITLE')),
            taskKey: updatedJob.id,
            action: {
              label: this.translate.instant('CREATE_MAP_DIALOG.OPEN_SMAP'),
              handler: (onClose) => {
                if (updatedJob.result?.entityId) {
                  const entityId = updatedJob.result.entityId;
                  onClose();
                  void this.router.navigate(['/dashboard/map', entityId]);
                }
              }
            }
          }
        );
      } else if (updatedJob.status === 'FAILED') {
        const rawError = updatedJob.error || '';
        if (rawError.startsWith(PDF_TOO_LARGE_JOB_PREFIX)) {
          showPdfTooLargeNotification(this.notificationService, this.translate, updatedJob.id);
          return;
        }
        const errorMessage = rawError || this.translate.instant(smapTaskKey(task, 'FAILED_DEFAULT'));
        this.notificationService.error(errorMessage, {
          title: this.translate.instant(smapTaskKey(task, 'FAILED_TITLE')),
          duration: 0,
          taskKey: updatedJob.id
        });
      }
    });
  }

  private handlePdfImport(file: File) {
    this.creditApiService.ensureCredits(this.dialog, 'smap').subscribe(ok => {
      if (!ok) {
        this.isImporting = false;
        return;
      }

      this.openaiService.uploadAndPreflightPdf(file).subscribe({
        next: ({s3Key, preflight}) => this.handlePdfPreflight(file, s3Key, preflight),
        error: (error) => {
          this.isImporting = false;
          if (isPdfTooLargeError(error)) {
            showPdfTooLargeNotification(this.notificationService, this.translate);
            return;
          }
          const errorMessage = error.error?.message || error.message || this.translate.instant(smapTaskKey('pdfConversion', 'START_FAILED'));
          this.notificationService.error(errorMessage, {
            title: this.translate.instant(smapTaskKey('pdfConversion', 'FAILED_TITLE')),
            duration: 0
          });
          console.error('PDF conversion error:', error);
        }
      });
    });
  }

  private handlePdfPreflight(file: File, s3Key: string, preflight: PreflightResponse) {
    switch (preflight.status) {
      case 'no_text':
        this.isImporting = false;
        this.notificationService.error(
          this.translate.instant('CREATE_MAP_DIALOG.PDF_NO_TEXT_MESSAGE'),
          {title: this.translate.instant('CREATE_MAP_DIALOG.PDF_CANNOT_SMAP_TITLE'), duration: 0}
        );
        return;
      case 'too_large':
        this.isImporting = false;
        showPdfTooLargeNotification(this.notificationService, this.translate);
        return;
      case 'needs_confirmation': {
        const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
          width: '540px',
          maxWidth: '90vw',
          data: {
            title: this.translate.instant('CREATE_MAP_DIALOG.PDF_PARTIAL_TITLE'),
            icon: 'info',
            message: this.translate.instant('CREATE_MAP_DIALOG.PDF_PARTIAL_MESSAGE', {percent: preflight.partialPercent}),
            confirmText: this.translate.instant('CREATE_MAP_DIALOG.PROCEED'),
            cancelText: this.translate.instant('CREATE_MAP_DIALOG.CANCEL'),
            showCancel: true,
          }
        });
        confirmRef.afterClosed().subscribe((confirmed) => {
          if (!confirmed) {
            this.isImporting = false;
            return;
          }
          this.startPdfConversion(file, s3Key, preflight.extractedKey);
        });
        return;
      }
      case 'fits':
        this.startPdfConversion(file, s3Key, preflight.extractedKey);
        return;
    }
  }

  private startPdfConversion(file: File, s3Key: string, extractedKey: string) {
    this.openaiService.startDocumentConversion(s3Key, file.name, extractedKey).subscribe({
      next: (job: Job) => {
        this.generationStateService.startSmapGeneration(job.id);
        this.notificationService.info(this.translate.instant(smapTaskKey('pdfConversion', 'IN_PROGRESS_INFO')), {
          duration: 8000,
          taskKey: job.id
        });
        this.trackImportJob(job, 'pdfConversion');
        this.matDialogRef.close();
      },
      error: (error) => {
        this.isImporting = false;
        if (isPdfTooLargeError(error)) {
          showPdfTooLargeNotification(this.notificationService, this.translate);
          return;
        }
        const errorMessage = error.error?.message || error.message || this.translate.instant(smapTaskKey('pdfConversion', 'START_FAILED'));
        this.notificationService.error(errorMessage, {
          title: this.translate.instant(smapTaskKey('pdfConversion', 'FAILED_TITLE')),
          duration: 0
        });
        console.error('PDF conversion error:', error);
      }
    });
  }
}
